import _Vue from 'vue';

export enum AppId {
    DREKI = 'dreki',
    HALVAR = 'halvar',
}

const isValidAppId = (id: any): boolean => ((id as string).toUpperCase() in AppId);

const appIdRaw = process.env.VUE_APP_ID;
if (!appIdRaw) {
    throw new Error(
        'No app id specified. Please set the VUE_APP_ID environment variable.',
    );
}
if (!isValidAppId(appIdRaw)) throw new Error('Invalid app id');

// global app config
export const APP_ID = appIdRaw as AppId;
export const APP_NAME = process.env.VUE_APP_NAME ?? 'App';

// global theme config
export const BREAKPOINTS = await import(`@/assets/${APP_ID}/theme/breakpoints.json`);
export const COLORS = await import(`@/assets/${APP_ID}/theme/colors.json`);

// global metrics config
const DERIVED_METRICS_PREFIX = process.env.VUE_APP_DERIVED_METRICS_PREFIX ?? '';
export function getDerivedMetricName(metric: string) {
    return `${DERIVED_METRICS_PREFIX}${metric}`;
}

// global background image
const context = require.context('@/assets', true, /background\.webp$/);
export const DEFAULT_BACKGROUND_IMAGE: string = context(`./${APP_ID}/img/background.webp`) ?? '@/assets/images/backgrounds/wind.jpg';

// global font
document.documentElement.classList.add(`app-font-${APP_ID}`);

// global feature flags
export const windyWeatherMapIsEnabled = process.env.VUE_APP_FEATURE_ENABLED_WINDY === 'true';
export const turbitHeatMapIsEnabled = process.env.VUE_APP_FEATURE_ENABLED_TURBIT_HEATMAP === 'true';
export const operatorForecastIsEnabled = process.env.VUE_APP_FEATURE_ENABLED_OPERATOR_FORECAST === 'true';
export const licenseAlertingIsEnabled = process.env.VUE_APP_LICENSE_ENABLED_ALERTING === 'true';
export const licenseAccountingIsEnabled = process.env.VUE_APP_LICENSE_ENABLED_ACCOUNTING === 'true';

// plugin
const ConfigPlugin = {
    install(vue: typeof _Vue) {
        const globalData = new _Vue({
            data: {
                $md: false,
            },
        });

        vue.prototype.$breakpoints = BREAKPOINTS;
        vue.prototype.$colors = COLORS;

        vue.mixin({
            computed: {
                $mx: { get: () => globalData.$data.$xs },
                $sm: { get: () => globalData.$data.$sm },
                $md: { get: () => globalData.$data.$md },
                $lg: { get: () => globalData.$data.$lg },
                $xl: { get: () => globalData.$data.$xl },
            },
        });
        this.calcBreakpoints(globalData);
        window.addEventListener('resize', () => this.calcBreakpoints(globalData), { passive: true });
    },

    calcBreakpoints(vue: _Vue) {
        vue.$data.$xs = window.innerWidth > BREAKPOINTS.xs;
        vue.$data.$sm = window.innerWidth > BREAKPOINTS.sm;
        vue.$data.$md = window.innerWidth > BREAKPOINTS.md;
        vue.$data.$lg = window.innerWidth > BREAKPOINTS.lg;
        vue.$data.$xl = window.innerWidth > BREAKPOINTS.xl;
    },
};

_Vue.use(ConfigPlugin);

// add type definitions for typescript compiler
declare module 'vue/types/vue' {
    interface Vue {
        $breakpoints: any;
        $colors: any;
        $xs: boolean;
        $sm: boolean;
        $md: boolean;
        $lg: boolean;
        $xl: boolean;
    }
}
